import {
  DefaultError,
  DefinedInitialDataOptions,
  QueryKey,
} from '@tanstack/react-query';
import queryString from 'query-string';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import { PaginatedResponse } from '../types';
import { useApiQuery } from './useApiQuery';

export type TableQueryParams = {
  pageIndex: number;
  pageSize: number;
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
};

export type UseTableDataQueryOptions<T> = {
  url: string;
  initialPageSize?: number;
  initialSearch?: string;
  debounceMs?: number;
  persistState?: boolean; // 👈 NEW optional flag
  queryOptions?: DefinedInitialDataOptions<
    PaginatedResponse<T>,
    DefaultError,
    PaginatedResponse<T>,
    QueryKey
  >;
  baseQueryKey?: unknown[];
};

export function useTableDataQuery<T>({
  url,
  initialPageSize = 20,
  initialSearch = '',
  debounceMs = 400,
  persistState = false, // save table state to sessionStorage
  queryOptions,
  baseQueryKey,
}: UseTableDataQueryOptions<T>) {
  const location = useLocation();

  const parentPath = location.pathname.split('/')[1] || '';
  const storageKey = `tableState_${parentPath}_${url}`;

  // --- Restore from sessionStorage if available ---
  const storedState = useMemo(() => {
    if (!persistState) return null;
    try {
      const saved = sessionStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  }, [storageKey, persistState]);

  const [isInitialMount, setIsInitialMount] = useState(true);

  const [pagination, setPagination] = useState(() => ({
    pageIndex: storedState?.pageIndex ?? 0,
    pageSize: storedState?.pageSize ?? initialPageSize,
  }));

  const [filters, setFilters] = useState<Record<string, any>>(
    () => storedState?.filters ?? {}
  );
  const [search, setSearch] = useState(storedState?.search ?? initialSearch);
  const [debouncedSearch] = useDebounce(search, debounceMs);

  const queryParams = useMemo(() => {
    const queryObject: Record<string, any> = {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      searchText: debouncedSearch || undefined,
      ...filters,
    };

    Object.keys(queryObject).forEach((key) => {
      const value = queryObject[key];
      if (
        value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)
      ) {
        delete queryObject[key];
      }
    });

    return queryString.stringify(queryObject, {
      arrayFormat: 'bracket',
      skipNull: true,
      skipEmptyString: true,
    });
  }, [pagination, filters, debouncedSearch]);

  const queryKey = [
    ...(baseQueryKey ?? [url]),
    pagination.pageIndex,
    debouncedSearch,
    JSON.stringify(filters),
  ];

  const { data, isFetching, isError, refetch, error, isLoading } = useApiQuery<
    PaginatedResponse<T>
  >(
    `${url}?${queryParams}`,
    {
      queryKey,
      ...queryOptions,
    },
    { method: 'GET' }
  );

  const hasFilters =
    !!debouncedSearch?.trim() ||
    Object.values(filters).some(
      (v) => v !== '' && v !== null && v !== undefined
    );

  const updateFilters = useCallback((newFilters: Record<string, any>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
  }, []);

  // --- Persist pagination + search in session storage ---
  useEffect(() => {
    if (!persistState) return;
    sessionStorage.setItem(
      storageKey,
      JSON.stringify({ ...pagination, search, filters })
    );
  }, [pagination, search, storageKey, filters, persistState]);

  // --- Reset state when parent route changes ---
  useEffect(() => {
    if (!persistState) return;
    const currentParent = location.pathname.split('/')[1] || '';
    const prevParent = sessionStorage.getItem('tableState_parent');
    if (prevParent && prevParent !== currentParent) {
      Object.keys(sessionStorage)
        .filter((k) => k.startsWith('tableState_'))
        .forEach((k) => sessionStorage.removeItem(k));
    }
    sessionStorage.setItem('tableState_parent', currentParent);
  }, [location.pathname, persistState]);

  // --- Reset to first page when search or filters change ---
  useEffect(() => {
    if (isInitialMount) return;
    setPagination((prev) =>
      prev.pageIndex !== 0 ? { ...prev, pageIndex: 0 } : prev
    );
  }, [debouncedSearch, JSON.stringify(filters)]);

  useEffect(() => {
    setIsInitialMount(false);
  }, []);

  return {
    data: data?.items ?? [],
    totalCount: data?.pagination.total ?? 0,
    storedState,
    pagination,
    onPaginationChange: setPagination,
    searchValue: search,
    onSearchChange: setSearch,
    filters,
    setFilters: updateFilters,
    refetch,
    isLoading,
    isFetching,
    isError,
    error,
    hasFilters,
  };
}
